import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Dev mode: set a mock user and skip auth
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      setUser({ email: 'dev@baluad.com', name: 'Dev User', plan: 'annual', plan_expires_at: '2099-12-31T00:00:00Z' });
      setLoading(false);
      return;
    }

    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    localStorage.setItem('auth_token', token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch {}
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  // Check if user has active plan
  const hasActivePlan = () => {
    if (!user?.plan || !user?.plan_expires_at) return false;
    return new Date(user.plan_expires_at) > new Date();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasActivePlan, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
